import * as Yup from 'yup'
import { camelCase } from 'lodash'

export const INTEGRATION_TYPES = {
  YARDI: 'yardi',
  REAL_PAGE: 'real-page',
  ENTRATA: 'entrata',
  RENT_MANAGER: 'rent-manager',
  RESMAN: 'resman',
  BRIVO: 'brivo',
  SALTO_SPACE: 'salto-space',
  SWIFTLANE: 'swiftlane',
  DORMAKABA: 'dormakaba',
  IGLOO_WORKS: 'igloo-works',
  ACCENTRA: 'accentra',
  BUTTERFLY: 'butterfly',
  REMOTE_LOCK: 'remote-lock',
  POINT_CENTRAL: 'point-central',
}

export const PMS_INTEGRATION_TYPES = [
  INTEGRATION_TYPES.YARDI,
  INTEGRATION_TYPES.REAL_PAGE,
  INTEGRATION_TYPES.ENTRATA,
  INTEGRATION_TYPES.RENT_MANAGER,
  INTEGRATION_TYPES.RESMAN,
]

export const DISCONNECTABLE_INTEGRATIONS = [
  INTEGRATION_TYPES.YARDI,
  INTEGRATION_TYPES.ENTRATA,
  INTEGRATION_TYPES.REAL_PAGE,
  INTEGRATION_TYPES.RENT_MANAGER,
  INTEGRATION_TYPES.RESMAN,
]

export const getPmsTypeOptions = (t) => [
  { value: INTEGRATION_TYPES.YARDI, label: t('organisation:places.integrations.pms.types.yardi') },
  {
    value: INTEGRATION_TYPES.REAL_PAGE,
    label: t('organisation:places.integrations.pms.types.realPage'),
  },
  {
    value: INTEGRATION_TYPES.ENTRATA,
    label: t('organisation:places.integrations.pms.types.entrata'),
  },
  {
    value: INTEGRATION_TYPES.RENT_MANAGER,
    label: t('organisation:places.integrations.pms.types.rentManager'),
  },
  {
    value: INTEGRATION_TYPES.RESMAN,
    label: t('organisation:places.integrations.pms.types.resman'),
    disabled: true,
  },
]

// Note: base/fallback labels for the providers not yet i18n'd (see
// getKeylessIntegrationLabels below) — Dormakaba/Igloo Works/Accentra/ButterflyMX/
// RemoteLock/PointCentral stay hardcoded English here until they get their own extraction
// pass; Brivo/Salto Space/Swiftlane are overridden with real translations there instead.
export const KEYLESS_INTEGRATION_LABELS = {
  [INTEGRATION_TYPES.BRIVO]: 'Brivo',
  [INTEGRATION_TYPES.SALTO_SPACE]: 'Salto Space',
  [INTEGRATION_TYPES.SWIFTLANE]: 'Swiftlane',
  [INTEGRATION_TYPES.DORMAKABA]: 'Dormakaba',
  [INTEGRATION_TYPES.IGLOO_WORKS]: 'Igloo Works',
  [INTEGRATION_TYPES.ACCENTRA]: 'Accentra',
  [INTEGRATION_TYPES.BUTTERFLY]: 'ButterflyMX',
  [INTEGRATION_TYPES.REMOTE_LOCK]: 'RemoteLock',
  [INTEGRATION_TYPES.POINT_CENTRAL]: 'PointCentral',
}

// Note: covers all 9 keyless-integration providers (KEYLESS_INTEGRATION_LABELS as the base),
// overriding only the 3 already i18n'd (Brivo/Salto Space/Swiftlane) with real translations —
// same keys `getKeylessIntegrationLabels` used pre-refactor (docs/i18n/README.md §10).
export const getKeylessIntegrationLabels = (t) => ({
  ...KEYLESS_INTEGRATION_LABELS,
  [INTEGRATION_TYPES.BRIVO]: t('organisation:places.integrations.keyless.types.brivo'),
  [INTEGRATION_TYPES.SALTO_SPACE]: t('organisation:places.integrations.keyless.types.saltoSpace'),
  [INTEGRATION_TYPES.SWIFTLANE]: t('organisation:places.integrations.keyless.types.swiftlane'),
})

export const pmsIntegrationInitialValues = {
  type: null,
  company: null,
  property: null,
}

export const getPmsIntegrationInitialValues = (building) => {
  const type = getPmsIntegrationType(building)
  return type
    ? {
        type,
        company: getCompanyId(building, type),
        property: getPropertyId(building, type),
      }
    : pmsIntegrationInitialValues
}

export const getCompanyId = (building, type) => {
  return building[`${camelCase(type)}ManagementId`]
}

export const getPropertyId = (building, type) => {
  return building[`${camelCase(type)}PropertyId`]
}

export const getPmsIntegrationType = (building) => {
  if (!building) return null
  return building.yardiPropertyId
    ? INTEGRATION_TYPES.YARDI
    : building.entrataPropertyId
      ? INTEGRATION_TYPES.ENTRATA
      : building.realPagePropertyId
        ? INTEGRATION_TYPES.REAL_PAGE
        : building.rentManagerPropertyId
          ? INTEGRATION_TYPES.RENT_MANAGER
          : building.resmanPropertyId
            ? INTEGRATION_TYPES.RESMAN
            : null
}

export const pmsValidationSchema = Yup.object().shape({
  type: Yup.string().oneOf(PMS_INTEGRATION_TYPES).required(),
  company: Yup.number().required(),
  property: Yup.string().required(),
})

export const getCompanyInitialValues = (type) => {
  switch (type) {
    case INTEGRATION_TYPES.YARDI:
      return {
        name: '',
        username: '',
        password: '',
        serverName: '',
        database: '',
        url: '',
      }
    case INTEGRATION_TYPES.ENTRATA:
      return {
        name: '',
        managementCompany: '',
        username: '',
        password: '',
      }
    case INTEGRATION_TYPES.REAL_PAGE:
      return {
        name: '',
        pmcid: '',
      }
    case INTEGRATION_TYPES.RENT_MANAGER:
      return {
        name: '',
        username: '',
        password: '',
        companyCode: '',
      }
    default:
      return {}
  }
}

export const getCompanyValidationSchema = (type) => {
  switch (type) {
    case INTEGRATION_TYPES.YARDI:
      return Yup.object().shape({
        name: Yup.string().required(),
        username: Yup.string().required(),
        password: Yup.string().required(),
        serverName: Yup.string().required(),
        database: Yup.string().required(),
        url: Yup.string().required(),
      })
    case INTEGRATION_TYPES.ENTRATA:
      return Yup.object().shape({
        name: Yup.string().required(),
        managementCompany: Yup.string().required(),
        username: Yup.string().required(),
        password: Yup.string().required(),
      })
    case INTEGRATION_TYPES.REAL_PAGE:
      return Yup.object().shape({
        name: Yup.string().required(),
        pmcid: Yup.string().required(),
      })
    case INTEGRATION_TYPES.RENT_MANAGER:
      return Yup.object().shape({
        name: Yup.string().required(),
        username: Yup.string().required(),
        password: Yup.string().required(),
        companyCode: Yup.string().required(),
      })
    default:
      return Yup.object().shape({})
  }
}

export const CONVENIENCE_FEE_TYPES = {
  CARD: 'card',
  ACH: 'ach',
}

export const CONVENIENCE_FEE_MODES = {
  FLAT: 'flat',
  PERCENT: 'percent',
}

export const CONVENIENCE_FEE_MAX_PERCENTAGE = 100

const isEmptyFeeValue = (value) => value === '' || value === null || value === undefined

// The form keeps a % and a $ input per payment type; whichever one is filled decides the mode
// (the inputs clear each other on change). Flat amounts travel in cents, the form edits dollars.
const getConvenienceFeeFormValues = (processingFees, type) => {
  const fee = processingFees.find((convenienceFee) => convenienceFee.type === type)

  if (fee?.mode === CONVENIENCE_FEE_MODES.FLAT) {
    return { percentage: '', flatAmount: (fee.flatAmount ?? 0) / 100 }
  }

  return { percentage: fee?.percentage ?? 0, flatAmount: '' }
}

const toConvenienceFee = (type, percentage, flatAmount) =>
  isEmptyFeeValue(flatAmount)
    ? { type, mode: CONVENIENCE_FEE_MODES.PERCENT, percentage: Number(percentage) }
    : { type, mode: CONVENIENCE_FEE_MODES.FLAT, flatAmount: Math.round(Number(flatAmount) * 100) }

export const getConvenienceFeesPayload = ({
  cardFeePercentage,
  cardFeeFlatAmount,
  achFeePercentage,
  achFeeFlatAmount,
}) => [
  toConvenienceFee(CONVENIENCE_FEE_TYPES.CARD, cardFeePercentage, cardFeeFlatAmount),
  toConvenienceFee(CONVENIENCE_FEE_TYPES.ACH, achFeePercentage, achFeeFlatAmount),
]

const convenienceFeeSchema = (t, max, counterpartField) => {
  let schema = Yup.number()
    .transform((value, originalValue) => (isEmptyFeeValue(originalValue) ? null : value))
    .nullable()
    .min(0, t('organisation:places.integrations.payRentConfig.validation.feeMin'))

  if (max !== null)
    schema = schema.max(
      max,
      t('organisation:places.integrations.payRentConfig.validation.feeMax', { max }),
    )

  return schema.test(
    'percentage-or-flat-amount',
    t('organisation:places.integrations.payRentConfig.validation.feeRequired'),
    function (value) {
      return value !== null || !isEmptyFeeValue(this.parent[counterpartField])
    },
  )
}

export const getPayRentConfigInitialValues = (paymentSettings) => {
  const { settings = {}, processingFees = [], chargeCodesMappings = [] } = paymentSettings
  const {
    allowAutopay = false,
    allowChargesSelection = false,
    allowPartialPayment = false,
    dailyBatch = false,
    prePaidChargeCode = null,
    amenityChargeCode = null,
    processingDelay = 0,
    syncAmenityCharges = false,
  } = settings
  const cardFee = getConvenienceFeeFormValues(processingFees, CONVENIENCE_FEE_TYPES.CARD)
  const achFee = getConvenienceFeeFormValues(processingFees, CONVENIENCE_FEE_TYPES.ACH)

  return {
    cardFeePercentage: cardFee.percentage,
    cardFeeFlatAmount: cardFee.flatAmount,
    achFeePercentage: achFee.percentage,
    achFeeFlatAmount: achFee.flatAmount,
    allowAutopay,
    allowChargesSelection,
    allowPartialPayment,
    dailyBatch,
    prePaidChargeCode,
    amenityChargeCode,
    processingDelay,
    syncAmenityCharges,
    chargeCodesMappings,
  }
}

export const getPayRentConfigValidationSchema = (t, isRentManager) =>
  Yup.object().shape({
    cardFeePercentage: convenienceFeeSchema(t, CONVENIENCE_FEE_MAX_PERCENTAGE, 'cardFeeFlatAmount'),
    cardFeeFlatAmount: convenienceFeeSchema(t, null, 'cardFeePercentage'),
    achFeePercentage: convenienceFeeSchema(t, CONVENIENCE_FEE_MAX_PERCENTAGE, 'achFeeFlatAmount'),
    achFeeFlatAmount: convenienceFeeSchema(t, null, 'achFeePercentage'),
    allowAutopay: Yup.boolean().required(),
    allowChargesSelection: Yup.boolean().required(),
    allowPartialPayment: Yup.boolean().required(),
    dailyBatch: Yup.boolean().required(),
    prePaidChargeCode: isRentManager
      ? Yup.string().nullable()
      : Yup.string()
          .nullable()
          .required(
            t(
              'organisation:places.integrations.payRentConfig.validation.prePaidChargeCodeRequired',
            ),
          ),
    amenityChargeCode: isRentManager
      ? Yup.string().nullable()
      : Yup.string()
          .nullable()
          .required(
            t(
              'organisation:places.integrations.payRentConfig.validation.amenityChargeCodeRequired',
            ),
          ),
    processingDelay: Yup.number().required(
      t('organisation:places.integrations.payRentConfig.validation.processingDelayRequired'),
    ),
    syncAmenityCharges: Yup.boolean().required(),
    chargeCodesMappings: Yup.array()
      .of(
        Yup.object().shape({
          code: Yup.string().required(),
          description: Yup.string().required(
            t('organisation:places.integrations.payRentConfig.validation.descriptionRequired'),
          ),
        }),
      )
      .required(
        t('organisation:places.integrations.payRentConfig.validation.chargeCodesMappingsRequired'),
      ),
  })

// `iglooWorkStaffAccessAvailable` has no editable control of its own — it is derived from
// `iglooWorkAvailable` + `keylessAccessForStaffAvailable` (Product decision, Kirill 2026-07-27:
// staff keyless is one generic toggle covering every integration). The building PUT flips staff
// Igloo access on an edge-triggered backend transition (`before && !after` / `!before && after`
// in KeylessService.applyKeylessChangesOnBuildingUpdate), so an unconditional resend of a value
// that still matches server truth is harmless — but a resend that has silently drifted from it
// is not: it fires a real enable (mass-mints a non-recallable eKey per eligible staff member) or
// disable (deletes every staff key on the building) the PM never asked for. Only include the key
// in the payload when the freshly-derived value actually differs from the value read from the
// server at form-load time.
export const getIglooWorkStaffAccessAvailablePatch = ({
  iglooWorkAvailable,
  keylessAccessForStaffAvailable,
  serverIglooWorkStaffAccessAvailable,
}) => {
  const derived = iglooWorkAvailable ? keylessAccessForStaffAvailable : false
  return derived !== (serverIglooWorkStaffAccessAvailable || false)
    ? { iglooWorkStaffAccessAvailable: derived }
    : {}
}

// Keeps only digits and the first dot, then truncates (doesn't round) to `decimals` places.
// Truncation happens on the string so values like 1.15 don't drift to 1.14 via float math.
export const toFloatWithDecimals = (input, decimals) => {
  if (input === null || input === '') return null

  let cleaned = ''
  let dotFound = false

  for (const char of String(input)) {
    if (char >= '0' && char <= '9') {
      cleaned += char
    } else if (char === '.' && !dotFound) {
      cleaned += '.'
      dotFound = true
    }
  }

  // If the value is empty or a dot, return null
  if (cleaned === '' || cleaned === '.') return null

  const [integerPart, fractionPart = ''] = cleaned.split('.')
  const num = parseFloat(`${integerPart || '0'}.${fractionPart.slice(0, decimals) || '0'}`)

  return isNaN(num) ? null : num
}
